import React, {useState, useContext, useEffect} from "react";
import {get, filter, includes} from "lodash";
import {SelectBox} from "../../../Components/Pickers/SelectBox/SelectBox";
import {MultiSelectAutocomplete} from "../../../Components/Form/AutoComplete/AutoComplete";
import './CompanyFilter.scss'
import {I18nReactContext} from "../../../contexts/I18nContext";

export const CompanyFilter = ({filterModel, setFilterModel, disabledObj, enableMultiSelect = false, enableSelectAll = true}) => {
    const {i18n} = useContext(I18nReactContext);
    
    // Initialize selected companies based on mode
    const [selectedCompanies, setSelectedCompanies] = useState(() => {
        if (enableMultiSelect) {
            const companyIds = get(filterModel, 'filterValues.companyIds', []);
            const companiesData = get(filterModel, 'filterData.companies', []);
            return filter(companiesData, comp => includes(companyIds, comp.value));
        }
        return get(filterModel, 'filterValues.companyId');
    });

    // Update local state when filterModel changes
    useEffect(() => {
        if (enableMultiSelect) {
            const companyIds = get(filterModel, 'filterValues.companyIds', []);
            const companiesData = get(filterModel, 'filterData.companies', []);
            const newSelectedCompanies = filter(companiesData, comp => includes(companyIds, comp.value));
            setSelectedCompanies(newSelectedCompanies);
        } else {
            setSelectedCompanies(get(filterModel, 'filterValues.companyId'));
        }
    }, [filterModel, enableMultiSelect]);

    // Local filter model state for debouncing
    const [localFilterModel, setLocalFilterModel] = useState(filterModel);
    
    // Debounce the filter model updates to parent
    useEffect(() => {
        const handler = setTimeout(() => {
            setFilterModel(localFilterModel);
        }, 1000); // 1 second debounce

        return () => {
            clearTimeout(handler);
        };
    }, [localFilterModel, setFilterModel]);

    const onSelectCompanyChange = (e) => {
        const value = e.target.value;
        setSelectedCompanies(value);
        
        const newFilterModel = {
            ...filterModel,
            filterValues: {
                ...filterModel.filterValues,
                companyId: value
            }
        }
        setLocalFilterModel(newFilterModel);
    }

    const onSelectCompaniesChange = (selectedComps) => {
        // Prevent empty selections - if user tries to deselect all, keep at least "All Companies" if available
        // or just allow empty which might mean "all" depending on backend logic, but usually we want explicit selection
        
        setSelectedCompanies(selectedComps);
        
        const newFilterModel = {
            ...filterModel,
            filterValues: {
                ...filterModel.filterValues,
                companyIds: selectedComps.map(comp => comp.value),
            }
        }
        setLocalFilterModel(newFilterModel);
    }

    const getFilteredCompanies = ({query}) => {
        const companies = get(filterModel, 'filterData.companies', []);
        
        if (!query || query.trim().length === 0) {
            return companies;
        }
        
        return filter(companies, comp => 
            comp.text.toLowerCase().includes(query.toLowerCase())
        );
    }

    if (enableMultiSelect) {
        return (
            <div className="searchFilter">
                <MultiSelectAutocomplete
                    selectAllText={i18n.translate('editAdminUserModal.selectAllOrgs')} // Reusing existing translation or add new one
                    enableSelectAll={enableSelectAll}
                    label={i18n.translate('dataGrid.filterLabels.company')}
                    fetchOptions={getFilteredCompanies}
                    initialSelectedValues={selectedCompanies}
                    onSelectionChange={onSelectCompaniesChange}
                    disabled={get(disabledObj, 'disabled')}
                />
                {get(disabledObj, 'disabled') ? <div className="disabledNotice">{get(disabledObj, 'disabledNote')}</div> : ''}
            </div>
        );
    }

    return <div className="searchFilter">
        <SelectBox 
            label={i18n.translate('dataGrid.filterLabels.company')} 
            disabled={get(disabledObj, 'disabled')} 
            options={filterModel.filterData.companies} 
            value={selectedCompanies} 
            onChange={onSelectCompanyChange}
        />
        {get(disabledObj, 'disabled') ? <div className="disabledNotice">{get(disabledObj, 'disabledNote')}</div> : ''}
    </div>

}
